/**
 * Apple Wallet wizard — the flow's own local draft: which step is showing,
 * the two identifiers every minted pass carries, the two drafted PEM fields,
 * and the live outcome of the one substantive call this wizard
 * makes. Mountable and zero-I/O: the actual save runs through
 * `appleWalletSaveIntent$` below, answered by a mounted app-side `.sync`
 * (web/src/admin/settings/integrations/appleWallet/wizard.sync.ts)
 * that stores the identifiers, forwards the credential straight into the
 * EXISTING saveIntegrationAction$
 * (web/src/admin/settings/integrations/integrations.state.ts) and
 * writes the outcome it produces back onto `appleWalletWizardDraft` — this
 * file never reaches saveIntegrationAction$ itself, and never reaches
 * Firestore.
 *
 * Three steps — Setup, Credentials, Done. Setup is a runbook the operator
 * follows elsewhere (Apple's portal, a terminal) and holds no draft state of
 * its own: nothing there can be verified from this browser, and the only
 * proof either portal visit succeeded is Credentials' own Save & verify
 * call. Credentials absorbs that whole lifecycle — paste, save, the outcome
 * rendered in place — because saveIntegration proves and stores atomically
 * in one call. Both PEM fields lock for the call's duration (`saving`), so a
 * field cannot be edited out from under an in-flight attempt and no stale
 * pair can exist to detect.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { IntegrationSaveFailure } from "@momo/types";
import { Subject } from "rxjs";

export type AppleWalletWizardStep = 1 | 2 | 3;

/** The Credentials step's one substantive call, classified. `idle` covers
 *  both "never attempted this session" and "fields edited since the last
 *  attempt" — nothing here needs to distinguish the two, since a locked
 *  field makes stale-vs-fresh moot: nothing can change while `saving` is
 *  true, and a fresh mount always starts idle. */
export type AppleWalletWizardOutcome =
	| { readonly kind: "idle" }
	| {
			readonly kind: "verificationFailed";
			readonly reason: IntegrationSaveFailure.IntegrationVerificationReason;
			readonly detail: string;
	  }
	| {
			readonly kind: "transportFailed";
			readonly reason: IntegrationSaveFailure.Reason;
	  }
	| {
			readonly kind: "success";
			/** The just-proved certificate's own expiry, ISO-8601 — read off
			 *  this save's own SaveIntegrationOutcome (never re-derived), null
			 *  only if that field was somehow absent from an otherwise-passed
			 *  verification. */
			readonly certificateExpiresAt: string | null;
	  };

/** The two identifiers Apple's own portal issues alongside the certificate.
 *  They are not credentials — every issued pass carries both in the clear —
 *  but a pass task runs only when both are stored, so the wizard that
 *  collects the certificate collects these in the same breath. */
export type AppleWalletPassIdentity = {
	readonly passTypeId: string;
	readonly teamId: string;
};

export type AppleWalletWizardDraft = AppleWalletPassIdentity & {
	readonly step: AppleWalletWizardStep;
	readonly certificatePem: string;
	readonly wwdrPem: string;
	/** Whether a certificate/WWDR pair is already stored for this workspace —
	 *  seeded by the mounting card through openAppleWalletWizard. What makes
	 *  a left-blank PEM field mean "keep the stored one", so the Pass Type ID
	 *  certificate rotates without re-pasting the far-less-often-rotated WWDR
	 *  intermediate beside it, and vice versa. */
	readonly hasStoredCredential: boolean;
	readonly saving: boolean;
	readonly outcome: AppleWalletWizardOutcome;
};

function initialDraft(): AppleWalletWizardDraft {
	return {
		step: 1,
		passTypeId: "",
		teamId: "",
		certificatePem: "",
		wwdrPem: "",
		hasStoredCredential: false,
		saving: false,
		outcome: { kind: "idle" },
	};
}

export const appleWalletWizardDraft = state<AppleWalletWizardDraft>(
	"integrations/appleWallet/wizard-draft",
).memory(initialDraft());

/** Resets the draft for a fresh wizard mount — called from the view's own
 *  connectedCallback so a reopened wizard never carries a prior session's
 *  pasted PEMs or outcome. `stored` seeds the two identifiers
 *  from whatever this workspace already has, so a rotation retypes neither
 *  and cannot mistype one into a value the stored certificate was never
 *  issued for; the PEMs are deliberately NOT seeded — nothing ever reads a
 *  stored credential back, which is exactly why `hasStoredCredential` has to
 *  travel separately for a blank field to mean "keep". */
export function openAppleWalletWizard(
	stored: AppleWalletPassIdentity = { passTypeId: "", teamId: "" },
	hasStoredCredential = false,
): void {
	appleWalletWizardDraft.replace({
		...initialDraft(),
		...stored,
		hasStoredCredential,
	});
}

function looksLikePem(value: string): boolean {
	return value.trim().startsWith("-----BEGIN");
}

/** Gates the Save & verify button — a structural, honest sanity check on
 *  the operator's own paste (catches a truncated clipboard or the wrong
 *  exported file before a round trip) plus the presence of both
 *  identifiers, never a guess at which field a later server-side failure
 *  names. A PEM field left blank is that check's one exemption, and only
 *  where a stored pair exists for it to keep: with nothing stored the save
 *  stays whole-credential, both PEMs required. */
export const appleWalletSaveReady = computed<boolean>(() => {
	const draft = appleWalletWizardDraft.value;
	if (draft.passTypeId.trim().length === 0) return false;
	if (draft.teamId.trim().length === 0) return false;
	const certificateBlank = draft.certificatePem.trim().length === 0;
	if (certificateBlank && !draft.hasStoredCredential) return false;
	if (!certificateBlank && !looksLikePem(draft.certificatePem)) return false;
	const wwdrBlank = draft.wwdrPem.trim().length === 0;
	if (wwdrBlank && !draft.hasStoredCredential) return false;
	if (!wwdrBlank && !looksLikePem(draft.wwdrPem)) return false;
	return true;
});

/** The wizard's one save intent — wizard.sync.ts (web) is the sole
 *  subscriber: it stores the identity, forwards the credential into the
 *  existing saveIntegrationAction$ and writes the outcome back onto the
 *  draft above once that shared channel settles. Each PEM is a KEY only
 *  when the operator actually pasted one; an absent key is the save
 *  contract's own "keep the stored value" signal, which an empty string is
 *  not (every declared secret field is `.min(1)`). */
export const appleWalletSaveIntent$ = new Subject<
	AppleWalletPassIdentity & {
		/** The organization the wizard opened under; a save after a switch
		 *  names it, so it lands there or nowhere. */
		readonly orgId: string | null;
		readonly certificatePem?: string;
		readonly wwdrPem?: string;
	}
>();
